import { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Stack,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import AuthField from "../lotto/AuthField";
import AppDialog from "../lotto/AppDialog";
import ForgotPasswordForm from "./ForgotPasswordForm";
import { LoginParams } from "../../api";
import { useAuth } from "../../AuthProvider";
import { useCustomColors } from "../../hooks/useCustomColors";
import toastStyles from "../../utils/styles";
import { PATTERNS, secureString } from "../../utils/validation";
import { useSecureInput } from "../../hooks/useSecureInput";
import { getAuthErrorMessage } from "../../utils/apiError";

// Hidden for now, not removed: players reset through support. Flip to show the
// link again; it opens the Forgot Password pop-up over the login card.
const SHOW_FORGOT_PASSWORD = false;

interface LoginFormProps {
  /** Fired after a successful login, before the usual redirect. Lets the
   *  mobile dialog close itself without changing the auth flow. */
  onSuccess?: () => void;
}

const LoginForm = ({ onSuccess }: LoginFormProps) => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t } = useTranslation();
  const colors = useCustomColors();
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);

  const loginSchema = Yup.object().shape({
    username: secureString(t, { maxLength: 64 })
      .required(t("validation_username_required"))
      .min(4, t("validation_username_min"))
      .matches(PATTERNS.username, t("validation_username_format")),
    password: secureString(t, { kind: "password", maxLength: 64 })
      .required(t("validation_password_required"))
      .min(8, t("validation_password_min"))
      .matches(PATTERNS.password, t("validation_password_format")),
  });

  const { control, handleSubmit } = useForm<LoginParams>({
    resolver: yupResolver(loginSchema),
    defaultValues: { username: "", password: "" },
  });
  const username = useSecureInput({ name: "username", control, maxLength: 64 });
  const password = useSecureInput({ name: "password", control, kind: "password", maxLength: 64 });

  const onSubmit = async (data: LoginParams) => {
    setIsLoading(true);

    try {
      const response = await login(data);

      if (response && response.status === 1) {
        toast.success(t("toast_login_success"));
        onSuccess?.();
        navigate("/");
      } else {
        toast.error(t("toast_login_failed"));
      }
    } catch (err) {
      toast.error(getAuthErrorMessage(err, t, "toast_login_failed"));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.textContent = toastStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  return (
    <>
      <Box component="form" onSubmit={handleSubmit(onSubmit)}>
        <AuthField
          label={t("sign_up_username")}
          icon={PersonRoundedIcon}
          placeholder={t("sign_up_username_placeholder")}
          autoComplete="username"
          {...username}
        />
        <AuthField
          label={t("login_password")}
          icon={LockRoundedIcon}
          type={showPassword ? "text" : "password"}
          placeholder={t("login_password_placeholder")}
          autoComplete="current-password"
          {...password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => setShowPassword(!showPassword)}
                  edge="end"
                  sx={{ color: colors.text.secondary }}
                >
                  {showPassword ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        {SHOW_FORGOT_PASSWORD && (
          <Box sx={{ mt: "-6px", mb: "12px", textAlign: "right" }}>
            <Link
              component="button"
              type="button"
              underline="none"
              onClick={() => setForgotOpen(true)}
              sx={{ fontSize: "12.5px", fontWeight: 700, color: colors.accent.gold }}
            >
              {t("login_forgot_password")}
            </Link>
          </Box>
        )}

        <Button
          type="submit"
          fullWidth
          disabled={isLoading}
          sx={{ mt: "8px", height: "52px", borderRadius: "14px" }}
        >
          {isLoading ? (
            <Stack direction="row" alignItems="center" sx={{ gap: 1 }}>
              <CircularProgress size={18} color="inherit" />
              {t("login_signing_in")}
            </Stack>
          ) : (
            t("login_sign_in")
          )}
        </Button>
      </Box>

      <AppDialog
        open={forgotOpen}
        onClose={() => setForgotOpen(false)}
        title={t("forgot_title")}
        subtitle={t("forgot_subtitle")}
      >
        <ForgotPasswordForm />
      </AppDialog>

      <ToastContainer
        position="top-right"
        autoClose={3000}
        closeOnClick
        pauseOnHover
        theme="dark"
      />
    </>
  );
};

export default LoginForm;
